import React__default, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import usePrevious from '../../hooks/usePrevious.js';

const BASE_CLASS = 'nui-portal';
function maybeRemoveClass(classList, className) {
  if (className != null && className !== '') {
    classList.remove(...className.split(' '));
  }
}
function maybeAddClass(classList, className) {
  if (className != null && className !== '') {
    classList.add(...className.split(' '));
  }
}
const portalPropTypes = {
  /** contents to send through the portal */
  children: PropTypes.node.isRequired,
  /** the HTML element that children will be mounted to */
  container: typeof window !== 'undefined' ? PropTypes.instanceOf(Element) : PropTypes.shape({}),
  /** className to add to a portal wrapper */
  className: PropTypes.string,
  /** callback invoked when the children of this Portal have been added to the DOM */
  onChildrenMount: PropTypes.func
};
/**
 * This component detaches its contents and re-attaches them to document.body.
 * Use it when you need to circumvent DOM z-stacking (for dialogs, popovers, etc.).
 * Any class names passed to this element will be propagated to the new container element on document.body.
 */
function Portal({
  className,
  container = document?.body,
  onChildrenMount,
  children
}) {
  const [portalElement, setPortalElement] = useState();
  useEffect(() => {
    if (!container) return undefined;
    const element = document.createElement('div');
    element.classList.add(BASE_CLASS);
    maybeAddClass(element.classList, className); // directly added to this portal element
    container.appendChild(element);
    setPortalElement(element);
    onChildrenMount?.();
    return () => {
      element.remove();
      setPortalElement(undefined);
    };
  }, [container]);
  const prevClassName = usePrevious(className);
  useEffect(() => {
    if (portalElement) {
      maybeRemoveClass(portalElement.classList, prevClassName);
      maybeAddClass(portalElement.classList, className);
    }
  }, [className]);

  // Only render `children` once this component has mounted in a browser environment, so they are
  // immediately attached to the DOM tree and can do DOM things like measuring or `autoFocus`.
  // See long comment on componentDidMount in https://reactjs.org/docs/portals.html#event-bubbling-through-portals
  if (typeof document === 'undefined' || !portalElement) {
    // <></> are needed for `react-docgen` to recognize this as a React component and get prop types for docs
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, null);
  }
  return /*#__PURE__*/createPortal(children, portalElement);
}
Portal.propTypes = portalPropTypes;
Portal.displayName = 'NuiPortal';

export { Portal as default };
